import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import styles from './ConfirmationBanner.module.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGlobalSession } from '../../../contexts/GlobalSessionContext';

interface ConfirmationBannerProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning';
}

export const ConfirmationBanner: React.FC<ConfirmationBannerProps> = ({
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger'
}) => {
  return (
    <div className={`${styles.banner} ${styles[variant]}`}>
      <div className={styles.content}>
        <div className={styles.iconSection}>
          <AlertTriangle className={styles.icon} />
        </div>
        <div className={styles.textSection}>
          <h3 className={styles.title}>{title}</h3>
          <p className={styles.message}>{message}</p>
        </div>
        <div className={styles.actions}>
          <button type="button" onClick={onConfirm} className={styles.confirmButton}>
            {confirmText}
          </button>
          <button type="button" onClick={onCancel} className={styles.cancelButton}>
            {cancelText}
          </button>
        </div>
        <button type="button" onClick={onCancel} className={styles.closeButton} aria-label="Close">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export const DeleteConfirmationBanner: React.FC = () => {
  const { pendingDelete, confirmDelete, cancelDelete, sessions } = useGlobalSession();
  const navigate = useNavigate();
  const location = useLocation();

  if (!pendingDelete) return null;

  const handleConfirmDelete = async () => {
    // Check if we're currently viewing the session being deleted
    const isCurrentSession = location.pathname === `/${pendingDelete.sessionId}`;
    
    // Check if this is the last session
    const isLastSession = sessions.length === 1;

    // Delete the session
    await confirmDelete();

    // If we deleted the current session OR it was the last session, navigate to /chat
    if (isCurrentSession || isLastSession) {
      navigate('/chat', { replace: true });
    }
  };

  return (
    <ConfirmationBanner
      title="Delete Chat Session"
      message={`Are you sure you want to permanently delete "${pendingDelete.title}"? This action cannot be undone.`}
      onConfirm={handleConfirmDelete}
      onCancel={cancelDelete}
      confirmText="Delete Forever"
      cancelText="Cancel"
      variant="danger"
    />
  );
};